import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { rem, vwDesktop, vwMobile } from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import SectionLines from "../SectionLines";
import { PauseIcon, PlayIcon } from "../shared/MediaControlIcons";
import { useCarouselMediaPreload } from "./hooks/useCarouselMediaPreload";
import { useCopyMinHeight } from "./hooks/useCopyMinHeight";
import { useInlineVideoPlayback } from "./hooks/useInlineVideoPlayback";
import { useSlideTransition } from "./hooks/useSlideTransition";
import { useVideoModalA11y } from "./hooks/useVideoModalA11y";
import type { SlideTransitionPhase } from "./hooks/useSlideTransition";

export type IngredientsCarouselMedia = {
  type: "video" | "image";
  isGif?: boolean;
  url: string;
  fallbackUrl?: string;
  alt?: string;
};

export type IngredientsCarouselSlide = {
  title: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  media?: IngredientsCarouselMedia;
};

export type IngredientsCarouselProps = {
  slides: IngredientsCarouselSlide[];
  autoRotateMs?: number;
  className?: string;
};

const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "mov", "m4v"];
const SLIDE_FADE_DURATION_MS = 260;

export const getMediaTypeFromFilename = (filename = ""): IngredientsCarouselMedia["type"] => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return VIDEO_EXTENSIONS.includes(ext) ? "video" : "image";
};

const IngredientsCarousel: FC<IngredientsCarouselProps> = ({
  slides,
  autoRotateMs = 10000,
  className,
}: IngredientsCarouselProps) => {
  const normalizedSlides = useMemo(
    () => (slides || []).filter((s) => s?.title || s?.description || s?.media?.url).slice(0, 3),
    [slides],
  );

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [slideElapsedMs, setSlideElapsedMs] = useState(0);
  const [useFallbackImage, setUseFallbackImage] = useState(false);
  const [resolvedImageUrls, setResolvedImageUrls] = useState<Record<string, string>>({});

  const inlineVideoRef = useRef<HTMLVideoElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const timerWindowRef = useRef<{ startMs: number; deadlineMs: number } | null>(null);
  const pausedAtMsRef = useRef<number | null>(null);
  const timerSessionRef = useRef(0);
  const componentIdRef = useRef(
    `ingredients-carousel-${Math.random().toString(36).slice(2, 9)}`,
  );

  const carouselId = componentIdRef.current;
  const headingId = `${carouselId}-title`;
  const modalTitleId = `${carouselId}-video-title`;

  const activeSlide = normalizedSlides[activeSlideIndex];
  const activeMedia = activeSlide?.media;
  const activeTitle = (activeSlide?.title || "").replace(/<[^>]+>/g, "").trim();
  const activeCtaLabel = (activeSlide?.ctaLabel || "Learn more").trim();
  const ctaAriaLabel = activeTitle
    ? `${activeCtaLabel} about ${activeTitle}`
    : activeCtaLabel;
  const isGifSlide = activeMedia?.type === "image" && !!activeMedia?.isGif;
  const resolvedActiveImageUrl =
    activeMedia?.type === "image" ? resolvedImageUrls[activeMedia.url] : "";
  const activeImageSrc =
    activeMedia?.type === "image" && useFallbackImage && activeMedia?.fallbackUrl
      ? activeMedia.fallbackUrl
      : resolvedActiveImageUrl || activeMedia?.url;
  const shouldShowPlayPause = !!activeMedia?.url;

  const { slideTransitionPhase, transitionToSlide, resetTransition } = useSlideTransition({
    activeSlideIndex,
    setActiveSlideIndex,
    durationMs: SLIDE_FADE_DURATION_MS,
  });

  useCarouselMediaPreload({
    slides: normalizedSlides,
    setResolvedImageUrls,
  });

  const { copyMinHeight, copyMeasureRefs } = useCopyMinHeight(normalizedSlides.length);

  useEffect(() => {
    if (activeSlideIndex > normalizedSlides.length - 1) {
      resetTransition();
      setActiveSlideIndex(0);
    }
  }, [activeSlideIndex, normalizedSlides.length, resetTransition]);

  const invalidateTimerSession = () => {
    timerSessionRef.current += 1;
  };

  useEffect(() => {
    const now = performance.now();
    invalidateTimerSession();
    timerWindowRef.current = {
      startMs: now,
      deadlineMs: now + autoRotateMs,
    };
    pausedAtMsRef.current = isPaused ? now : null;
    setSlideElapsedMs(0);
    setUseFallbackImage(false);
  }, [activeSlideIndex, activeMedia?.url, activeMedia?.type, autoRotateMs]);

  useEffect(() => {
    const timerWindow = timerWindowRef.current;
    if (!timerWindow) return;

    if (isPaused) {
      pausedAtMsRef.current = performance.now();
      return;
    }

    if (pausedAtMsRef.current !== null) {
      const pausedDurationMs = performance.now() - pausedAtMsRef.current;
      timerWindow.startMs += pausedDurationMs;
      timerWindow.deadlineMs += pausedDurationMs;
      pausedAtMsRef.current = null;
    }
  }, [isPaused]);

  useEffect(() => {
    if (isPaused || normalizedSlides.length <= 1) return;

    let timerWindow = timerWindowRef.current;
    if (!timerWindow) {
      const now = performance.now();
      timerWindow = {
        startMs: now,
        deadlineMs: now + autoRotateMs,
      };
      timerWindowRef.current = timerWindow;
    }

    const sessionId = timerSessionRef.current;
    const remainingMs = Math.max(0, timerWindow.deadlineMs - performance.now());
    let animationFrame = 0;

    const tick = (now: number) => {
      if (sessionId !== timerSessionRef.current) return;

      const activeTimer = timerWindowRef.current;
      if (!activeTimer) return;

      const elapsed = Math.min(autoRotateMs, Math.max(0, now - activeTimer.startMs));
      setSlideElapsedMs(elapsed);

      animationFrame = window.requestAnimationFrame(tick);
    };

    const timeout = window.setTimeout(() => {
      if (sessionId !== timerSessionRef.current) return;
      timerWindowRef.current = null;
      pausedAtMsRef.current = null;
      setSlideElapsedMs(0);
      const nextIndex =
        activeSlideIndex === normalizedSlides.length - 1 ? 0 : activeSlideIndex + 1;
      transitionToSlide(nextIndex);
    }, remainingMs);

    animationFrame = window.requestAnimationFrame(tick);
    return () => {
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [activeSlideIndex, autoRotateMs, isPaused, normalizedSlides.length]);

  const handleSelectSlide = (index: number) => {
    invalidateTimerSession();
    timerWindowRef.current = null;
    pausedAtMsRef.current = null;
    setSlideElapsedMs(0);
    transitionToSlide(index);
  };

  const handleTabListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!normalizedSlides.length) return;

    let nextIndex = activeSlideIndex;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      nextIndex =
        activeSlideIndex === normalizedSlides.length - 1 ? 0 : activeSlideIndex + 1;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      nextIndex =
        activeSlideIndex === 0 ? normalizedSlides.length - 1 : activeSlideIndex - 1;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextIndex = normalizedSlides.length - 1;
    } else {
      return;
    }

    handleSelectSlide(nextIndex);
  };

  const { handleTogglePause: togglePauseWithVideoState } = useInlineVideoPlayback({
    inlineVideoRef,
    isPaused,
    setIsPaused,
    activeSlideIndex,
    activeMediaType: activeMedia?.type,
    activeMediaUrl: activeMedia?.url,
  });

  const handleTogglePause = () => {
    invalidateTimerSession();
    togglePauseWithVideoState();
  };

  const slideProgress = Math.min(1, Math.max(0, slideElapsedMs / autoRotateMs));
  useVideoModalA11y({
    isOpen: isVideoModalOpen,
    dialogRef,
    triggerRef,
    onClose: () => setIsVideoModalOpen(false),
  });

  if (!normalizedSlides.length) return null;

  return (
    <>
      <Section className={className} aria-labelledby={headingId}>
        <Grid>
          <ContentCol>
            <DesktopContentFrame>
              <SlideSelectors
                role="tablist"
                aria-label="Ingredient slides"
                onKeyDown={handleTabListKeyDown}
              >
                {normalizedSlides.map((_, index) => (
                  <SelectorButton
                    key={`ingredient-slide-selector-${index}`}
                    id={`${carouselId}-tab-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={activeSlideIndex === index}
                    aria-controls={`${carouselId}-panel-${index}`}
                    tabIndex={0}
                    onClick={() => handleSelectSlide(index)}
                    $isActive={activeSlideIndex === index}
                  >
                    {index + 1}
                  </SelectorButton>
                ))}
              </SlideSelectors>

              <SlideFadeLayer $phase={slideTransitionPhase}>
                <CopyWrap
                  $minHeight={copyMinHeight}
                  id={`${carouselId}-panel-${activeSlideIndex}`}
                  role="tabpanel"
                  aria-labelledby={`${carouselId}-tab-${activeSlideIndex}`}
                >
                  <Headline id={headingId}>{activeSlide?.title}</Headline>
                  <Description
                    dangerouslySetInnerHTML={{
                      __html: activeSlide?.description || "",
                    }}
                  />
                  <Actions>
                    <PrimaryCta href={activeSlide?.ctaUrl} aria-label={ctaAriaLabel}>
                      {activeSlide?.ctaLabel}
                    </PrimaryCta>
                    {shouldShowPlayPause && (
                      <PlayPauseButton
                        type="button"
                        onClick={handleTogglePause}
                        aria-label={isPaused ? "Play ingredient carousel" : "Pause ingredient carousel"}
                        aria-pressed={isPaused}
                      >
                        <ProgressRing value={slideProgress} viewBox="0 0 43 43" aria-hidden="true">
                          <circle cx="21.5" cy="21.5" r="21" />
                        </ProgressRing>
                        {isPaused ? <PlayIcon /> : <PauseIcon />}
                      </PlayPauseButton>
                    )}
                  </Actions>
                </CopyWrap>
              </SlideFadeLayer>

              <CopyMeasureHost aria-hidden="true">
                {normalizedSlides.map((slide, index) => (
                  <CopyMeasureItem
                    key={`ingredient-copy-measure-${index}`}
                    ref={(node) => {
                      copyMeasureRefs.current[index] = node;
                    }}
                  >
                    <Headline>{slide?.title}</Headline>
                    <Description
                      dangerouslySetInnerHTML={{
                        __html: slide?.description || "",
                      }}
                    />
                    <Actions>
                      <PrimaryCta href={slide?.ctaUrl} tabIndex={-1}>
                        {slide?.ctaLabel}
                      </PrimaryCta>
                      {slide?.media?.url && (
                        <PlayPauseButton type="button" tabIndex={-1}>
                          <ProgressRing value={slideProgress} viewBox="0 0 43 43" aria-hidden="true">
                            <circle cx="21.5" cy="21.5" r="21" />
                          </ProgressRing>
                          <PauseIcon />
                        </PlayPauseButton>
                      )}
                    </Actions>
                  </CopyMeasureItem>
                ))}
              </CopyMeasureHost>
            </DesktopContentFrame>
          </ContentCol>

          <MediaCol>
            <MediaWrap>
              <MobileMediaRail $top={83.83} aria-hidden="true" />
              <MobileMediaRail $top={321.83} aria-hidden="true" />
              <MobileMediaRail $top={404.83} aria-hidden="true" />
              <CircleFrame aria-hidden="true" />
              <SlideFadeLayer $phase={slideTransitionPhase}>
                {activeMedia?.type === "video" ? (
                  <VideoMediaLayer key={`ingredient-media-layer-${activeSlideIndex}`}>
                    <MediaButton
                      key={`ingredient-media-video-${activeSlideIndex}`}
                      type="button"
                      ref={triggerRef}
                      onClick={() => setIsVideoModalOpen(true)}
                      aria-label={`Open video for slide ${activeSlideIndex + 1}`}
                    >
                      <CircularVideo
                        key={`ingredient-inline-video-${activeSlideIndex}`}
                        ref={inlineVideoRef}
                        src={activeMedia.url}
                        muted
                        loop
                        playsInline
                        autoPlay={!isPaused}
                        preload="metadata"
                      />
                    </MediaButton>
                  </VideoMediaLayer>
                ) : isGifSlide && activeMedia?.url ? (
                  <GifMediaLayer key={`ingredient-media-layer-${activeSlideIndex}`}>
                    <GifMedia
                      key={`ingredient-media-gif-${activeSlideIndex}`}
                      src={activeImageSrc}
                      alt={activeMedia?.alt || activeSlide?.title || "Ingredients"}
                      loading="eager"
                      decoding="sync"
                      fetchPriority="high"
                      onError={() => {
                        if (activeMedia?.fallbackUrl) setUseFallbackImage(true);
                      }}
                    />
                  </GifMediaLayer>
                ) : activeMedia?.url ? (
                  <ImageMediaLayer key={`ingredient-media-layer-${activeSlideIndex}`}>
                    <ImageMask>
                      <CircularImage
                        key={`ingredient-media-image-${activeSlideIndex}`}
                        src={activeImageSrc}
                        alt={activeMedia?.alt || activeSlide?.title || "Ingredients"}
                        loading="eager"
                        decoding="sync"
                        fetchPriority="high"
                        onError={() => {
                          if (activeMedia?.fallbackUrl) setUseFallbackImage(true);
                        }}
                      />
                    </ImageMask>
                  </ImageMediaLayer>
                ) : (
                  <ImageMediaLayer key={`ingredient-media-layer-${activeSlideIndex}`}>
                    <EmptyMedia />
                  </ImageMediaLayer>
                )}
              </SlideFadeLayer>
            </MediaWrap>
          </MediaCol>
        </Grid>
      </Section>

      {isVideoModalOpen && activeMedia?.type === "video" && (
        <Overlay
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsVideoModalOpen(false);
          }}
        >
          <Dialog
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            tabIndex={-1}
          >
            <VisuallyHidden id={modalTitleId}>
              {activeSlide?.title || "Ingredients video"}
            </VisuallyHidden>
            <CloseButton
              type="button"
              onClick={() => setIsVideoModalOpen(false)}
              aria-label="Close video"
            >
              ×
            </CloseButton>
            <ModalVideo controls autoPlay>
              <source src={activeMedia.url} />
              Your browser does not support the video tag.
            </ModalVideo>
          </Dialog>
        </Overlay>
      )}
    </>
  );
};

export default IngredientsCarousel;

const Section = styled.section`
  position: relative;
  background: #f6f1e7;
  overflow: hidden;
  padding-top: 0;
  padding-bottom: ${vwMobile(80)};

  ${MEDIA_DESKTOP} {
    padding-top: ${vwDesktop(80)};
    padding-bottom: ${vwDesktop(80)};
  }

  ${SectionLines("#012604")};
`;

const Grid = styled.div`
  position: relative;
  border-top: 1px solid #012604;
  border-bottom: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${vwMobile(50)};
  align-items: center;

  ${MEDIA_DESKTOP} {
    border-bottom: 1px solid #012604;
    grid-template-columns: 1fr 1fr;
    gap: 0;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      height: 1px;
      background: #012604;
      pointer-events: none;
      z-index: 1000;
    }

    &::before {
      top: ${vwDesktop(115.5)};
    }

    &::after {
      top: ${vwDesktop(608.5)};
    }
  }
`;

const ContentCol = styled.div`
  position: relative;
  order: 2;
  padding: 0 ${vwMobile(23.5)};
  display: flex;
  flex-direction: column;
  align-items: center;

  ${MEDIA_DESKTOP} {
    order: 0;
    min-height: ${vwDesktop(725)};
    padding: 0;
    justify-content: center;
    align-items: stretch;
  }
`;

const DesktopContentFrame = styled.div`
  width: ${vwMobile(313)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${vwDesktop(50)};
    width: ${vwDesktop(720)};
    padding-left: ${vwDesktop(128)};
    padding-right: ${vwDesktop(48)};
  }
`;

const SlideSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: ${vwMobile(12)};

  ${MEDIA_DESKTOP} {
    gap: ${vwDesktop(12)};
  }
`;

const SelectorButton = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: 50%;
  border: ${rem(0.5)} solid #012604;
  background: ${(p) => (p.$isActive ? "#012604" : "transparent")};
  color: ${(p) => (p.$isActive ? "#b5dd71" : "#012604")};

  font-family: "GT Alpina";
  font-weight: 300;
  font-size: ${rem(16)};
  line-height: 1;
  letter-spacing: ${rem(-0.64)};
  padding: 0;
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$isActive ? "#012604" : "#b5dd71")};
    color: ${(p) => (p.$isActive ? "#b5dd71" : "#012604")};
    border: ${rem(0.5)} solid ${(p) => (p.$isActive ? "#012604" : "#b5dd71")};
  }

  &:focus-visible {
    outline: ${rem(2)} solid #012604;
    outline-offset: ${rem(2)};
  }
`;

const CopyWrap = styled.div<{ $minHeight?: number }>`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width:${vwMobile(303)};
  gap: ${vwMobile(20)};
  min-height: ${(p) => (p.$minHeight ? `${p.$minHeight}px` : "auto")};

  ${MEDIA_DESKTOP} {
    margin-top: 0;
    align-items: flex-start;
    gap: ${vwDesktop(40)};
    width: ${vwDesktop(544)};
  }
`;

const SlideFadeLayer = styled.div<{ $phase: SlideTransitionPhase }>`
  opacity: ${(p) => (p.$phase === "fadeOut" ? 0 : 1)};
  transition: opacity ${SLIDE_FADE_DURATION_MS}ms ease;
`;

const CopyMeasureHost = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
`;

const CopyMeasureItem = styled(CopyWrap)`
  min-height: auto;
  margin-bottom: ${vwDesktop(8)};
`;

const Headline = styled.h2`
  margin: 0;
  color: #012604;
  font-family: "GT Alpina";
  font-weight: 300;
  font-size: ${rem(48)};
  line-height: 100%;
  letter-spacing: ${rem(-2.4)};
  text-align: center;

  ${MEDIA_DESKTOP} {
    font-size: ${rem(72)};
    line-height: 95%;
    letter-spacing: ${rem(-3.6)};
    text-align: left;
  }
`;

const Description = styled.div`
  margin: 0;
  color: #012604;
  font-family: "Sharp Sans Display No1", sans-serif;
  font-weight: 500;
  font-size: ${rem(12)};
  line-height: 130%;
  text-align: center;

  p {
    margin: 0;
  }

  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    width: ${vwDesktop(438)};
    max-width: ${vwDesktop(438)};
    text-align: left;
  }
`;

const Actions = styled.div`
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${vwMobile(16)};

  ${MEDIA_DESKTOP} {
    margin-top: 0;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    gap: 0;
  }
`;

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${rem(431.177)};
  background: #b5dd71;
  color: #012604;
  text-decoration: none;

  font-family: "GT Alpina";
  font-weight: 300;
  font-size: ${rem(12)};
  line-height: 100%;
  letter-spacing: ${rem(-0.6)};

  height: ${vwMobile(35)};
  padding: 0 ${vwMobile(32)};

  ${MEDIA_DESKTOP} {
    height: auto;
    font-size: ${rem(16)};
    letter-spacing: ${rem(-0.64)};
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
  }

  &:hover {
    background: #012604;
    color: #b5dd71;
  }
`;

const PlayPauseButton = styled.button`
  position: relative;
  display: inline-flex;
  width: ${vwMobile(35)};
  height: ${vwMobile(35)};
  padding: ${vwMobile(9.767)} ${vwMobile(13.023)};
  justify-content: center;
  align-items: center;

  border: 0;
  border-radius: ${rem(431.177)};
  background: rgba(255, 251, 244, 0.5);
  color: #012604;
  cursor: pointer;

  ${MEDIA_DESKTOP} {
    width: ${rem(43)};
    height: ${rem(43)};
    padding: ${rem(12)} ${rem(16)};
  }

  & > svg:last-child {
    position: relative;
    z-index: 2;
  }
`;

const CIRCLE_CIRCUMFERENCE = 131.947;

const ProgressRing = styled.svg.attrs<{ value: number }>((p) => ({
  style: {
    ['--progress-offset' as string]: CIRCLE_CIRCUMFERENCE - CIRCLE_CIRCUMFERENCE * p.value,
  } as React.CSSProperties,
}))`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  transform: rotate(-90deg);
  z-index: 1;

  circle {
    fill: none;
    stroke: #012604;
    stroke-width: ${rem(1.2)};
    stroke-linecap: round;
    stroke-dasharray: ${CIRCLE_CIRCUMFERENCE};
    stroke-dashoffset: var(--progress-offset);
    transition: stroke-dashoffset 120ms linear;
  }
`;

const MediaCol = styled.div`
  position: relative;
  order: 1;
  padding: 0;
  overflow: visible;

  ${MEDIA_DESKTOP} {
    order: 0;
    height: ${vwDesktop(725)};
    padding: 0;
    overflow: hidden;
  }
`;

const MediaWrap = styled.div`
  position: relative;
  width: ${vwMobile(360.516)};
  height: ${vwMobile(409.481)};
  display: grid;
  place-items: center;
  margin: 0 auto;

  ${MEDIA_DESKTOP} {
    width: 100%;
    height: 100%;
    min-height: ${vwDesktop(725)};
    display: block;
    margin: 0;
  }
`;

const CircleFrame = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${vwMobile(276.5)};
  height: ${vwMobile(276.5)};
  border-radius: 50%;
  border: 1px solid #012604;
  pointer-events: none;
  z-index: 2;

  ${MEDIA_DESKTOP} {
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: ${vwDesktop(553)};
    height: ${vwDesktop(553)};
  }
`;

const VideoMediaLayer = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  top: ${vwMobile(32)};
  transform: translateX(-50%);
  width: ${vwMobile(338)};
  height: ${vwMobile(373)};
  aspect-ratio: 29 / 32;

  ${MEDIA_DESKTOP} {
    position: absolute;
    left: 50%;
    top: ${vwDesktop(0.5)};
    transform: translateX(-50%);
    margin-left: ${vwDesktop(-83.5)};
    width: ${vwDesktop(721)};
    height: ${vwDesktop(725)};
    aspect-ratio: 180 / 181;
  }
`;

const GifMediaLayer = styled.div`
  position: relative;
  z-index: 1;
  width: ${vwMobile(320)};
  height: ${vwMobile(180)};
  aspect-ratio: 16 / 9;

  ${MEDIA_DESKTOP} {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${vwDesktop(816)};
    height: ${vwDesktop(459)};
    aspect-ratio: 16 / 9;
  }
`;

const ImageMediaLayer = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${vwMobile(276.5)};
  height: ${vwMobile(276.5)};
  aspect-ratio: 1 / 1;

  ${MEDIA_DESKTOP} {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: ${vwDesktop(553)};
    height: ${vwDesktop(553)};
    aspect-ratio: 1 / 1;
  }
`;

const MobileMediaRail = styled.div<{ $top: number }>`
  position: absolute;
  left: ${vwMobile(-100.24)};
  top: ${(p) => vwMobile(p.$top)};
  width: ${vwMobile(1440)};
  height: ${rem(0.5)};
  background: #012604;
  pointer-events: none;
  z-index: 1000;

  ${MEDIA_DESKTOP} {
    display: none;
  }
`;

const MediaButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  line-height: 0;
`;

const CircularImage = styled.img`
  display: block;

  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const ImageMask = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
`;

const CircularVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
`;

const GifMedia = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  object-position: center center;
`;

const EmptyMedia = styled.div`
  width: 100%;
  height: 100%;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.65);
  display: grid;
  place-items: center;
  padding: ${vwMobile(16)};
`;

const Dialog = styled.div`
  width: min(960px, 100%);
  background: #0b0b0b;
  border-radius: ${rem(18)};
  position: relative;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${rem(10)};
  right: ${rem(10)};
  z-index: 2;

  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  cursor: pointer;
`;

const ModalVideo = styled.video`
  width: 100%;
  display: block;
  aspect-ratio: 16 / 9;
  background: #000;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: ${rem(-1)};
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
